/**
 * The Builder draft (docs/pdlc/builder.md, B1 and B2).
 *
 * A draft's questions are mfour-agents' QRE extraction items
 * (`_QRE_EXTRACTION_RESPONSE_FORMAT`, workers/survey/qre_import.py:1015-1300),
 * snake_case as mfour-agents emits them, so a publish payload is the draft with
 * no rename layer. Do not camelCase these fields: the publish endpoint reads
 * them by name.
 *
 * Pure. The text enums here are mirrored by the check constraints in
 * migrations/studio/pdlc_builder.sql; migration-drift.test.ts holds them equal.
 */
import { z } from 'zod';

import { LockoutSchema } from '@/lib/feasibility/engine/lockout';
import { CriterionSchema, QUOTA_DIMENSIONS } from '@/lib/feasibility/engine/request';

import {
    CalibrationSchema,
    QualityRulesSchema,
    BuilderAdminSchema,
    BuilderIncentiveSchema,
    BuilderSettingsSchema,
    TranslationsSchema,
} from './settings';
import { SurveyChainSchema } from './chains';
import { BuilderStudySchema } from './study';

export const DRAFT_SCHEMA_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// Enums shared with the migration
// ---------------------------------------------------------------------------

export const DRAFT_STATUSES = ['draft', 'published', 'archived'] as const;
export type DraftStatus = (typeof DRAFT_STATUSES)[number];

export const VERSION_SOURCES = ['edit', 'methodology', 'import', 'dani', 'sotg'] as const;
export type VersionSource = (typeof VERSION_SOURCES)[number];

export const PUBLISH_STATUSES = ['sending', 'published', 'failed'] as const;
export type PublishStatus = (typeof PUBLISH_STATUSES)[number];

export const LAUNCH_STATUSES = ['sending', 'fielding', 'in_review', 'failed'] as const;
export type LaunchStatus = (typeof LAUNCH_STATUSES)[number];

/** How a preview walk ended (preview.ts PreviewOutcome). */
export const PREVIEW_OUTCOMES = ['completed', 'screened_out'] as const;
export type PreviewOutcomeValue = (typeof PREVIEW_OUTCOMES)[number];

/** Who walked a preview: a person in the editor, or an agent through the API. */
export const PREVIEW_WALKERS = ['person', 'agent'] as const;
export type PreviewWalker = (typeof PREVIEW_WALKERS)[number];

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

/** Every type mfour-agents extracts. A draft may hold any of them. */
export const QUESTION_TYPES = [
    'single_select',
    'multiple_select',
    'short_answer',
    'image_upload',
    'image_capture',
    'image_display',
    'watch_video',
    'number_slider',
    'date_picker',
    'number_pad',
    'matrix_parent',
    'matrix_child',
    // The SOTG editor's other types (gap map S7). mfour-agents publishes each
    // to its SOTG type: apportionment (5), a grid with unique columns, barcode
    // scan (14), external survey (13), audio (7), moment-to-moment video (6),
    // text only (12), segmentation (20), location verification, GPS enabler.
    'point_allocation',
    'ranking',
    'barcode_scan',
    'external_survey',
    'listen_audio',
    'video_dial',
    'text_screen',
    'segmentation',
    'location_check',
    'gps_opt_in',
    // SOTG's section and page headers, so a survey built in SOTG (survey 278,
    // docs/pdlc/builder-api.md) round-trips through Builder. Published through
    // the developer portal; mfour-agents' publisher does not know them.
    'section',
    'page',
    // A point between questions that ends the survey, or goes to a later question,
    // when it is reached and its rule holds (SOTG's terminate point and goto, as its
    // staff place them). Published through the developer portal only.
    'end_point',
    'go_to_point',
] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

/**
 * The types Builder v1 can edit. The rest are kept as imported, marked
 * "Kept as imported", and still published (the publish code handles them).
 */
export const EDITABLE_QUESTION_TYPES: readonly QuestionType[] = [
    'single_select',
    'multiple_select',
    'short_answer',
    'number_slider',
    'number_pad',
    'date_picker',
    'matrix_parent',
    'matrix_child',
    'image_display',
    'point_allocation',
    'ranking',
    'barcode_scan',
    'external_survey',
    'listen_audio',
    'video_dial',
    'text_screen',
    'segmentation',
    'location_check',
    'gps_opt_in',
    'end_point',
    'go_to_point',
];

/** Types whose options are answer choices a respondent picks from (or splits points, or ranks). */
export const CHOICE_QUESTION_TYPES: readonly QuestionType[] = [
    'single_select',
    'multiple_select',
    'matrix_parent',
    'point_allocation',
    'ranking',
];

/** Types nobody answers: shown, computed, or a step the app takes. */
export const UNANSWERED_QUESTION_TYPES: readonly QuestionType[] = [
    'image_display',
    'text_screen',
    'section',
    'page',
    'segmentation',
    'gps_opt_in',
    'end_point',
    'go_to_point',
];

/** Types that ask the respondent for a photo or video (feasibility's mediaCapture). */
export const CAPTURE_QUESTION_TYPES: readonly QuestionType[] = ['image_upload', 'image_capture'];

/** Loop fields Builder keeps but does not edit. (Shared lists are edited since P6.) */
export const UNEDITABLE_QUESTION_FIELDS = [
    'per_respondent_slot_iteration',
    'per_block_iteration',
    'per_question_iteration',
] as const;

/** The question types a carried-forward list goes from and to (shared lists, P6). */
export const SHARED_LIST_TYPES: readonly QuestionType[] = ['single_select', 'multiple_select'];

export const QuestionOptionSchema = z
    .object({
        option_text: z.string(),
        option_code: z.number().int(),
        anchor: z.boolean().optional(),
        exclusive: z.boolean().optional(),
        specify: z.boolean().optional(),
        terminate: z.boolean().optional(),
        /** Longer text shown with the answer (SOTG `optionExtended`). */
        extended_text: z.string().max(10_000).optional(),
        /** Kept but not shown to respondents (SOTG `display = 0`). */
        hidden: z.literal(true).optional(),
    })
    .strict();
export type QuestionOption = z.infer<typeof QuestionOptionSchema>;

export const DISPLAY_LOGIC_OPERATORS = ['any', 'all', 'none'] as const;

export const DisplayLogicSchema = z
    .object({
        parent_question_id: z.string().min(1),
        parent_option_codes: z.array(z.number().int()),
        parent_matrix_row_text: z.array(z.string()).optional(),
        parent_matrix_row_codes: z.array(z.number().int()).optional(),
        operator: z.enum(DISPLAY_LOGIC_OPERATORS).optional(),
    })
    .strict();
export type DisplayLogic = z.infer<typeof DisplayLogicSchema>;

export const SHARED_LIST_MODES = ['include_selected', 'exclude_selected', 'always_show'] as const;

// ---------------------------------------------------------------------------
// The condition tree (gap map P7, package L1)
// ---------------------------------------------------------------------------
//
// One tree serves "show this question if", "end the survey after it if" and
// "skip to a later question when". A one-question answer rule is still stored
// as `display_logic` (the shape publish has always carried); the tree is used
// only for what `display_logic` cannot say. logic.ts evaluates it and
// validate.ts checks its references; mfour-agents renders it to SOTG's
// logicExpression at publish (docs/pdlc/builder.md, "Logic").

export const LOGIC_MATCHES = ['any', 'all', 'none'] as const;
export const LOGIC_COMPARE_OPS = ['eq', 'ne', 'gt', 'ge', 'lt', 'le'] as const;
export type LogicCompareOp = (typeof LOGIC_COMPARE_OPS)[number];
/** all = every item holds; any = at least one; none = no item holds. */
export const LOGIC_JOINS = ['all', 'any', 'none'] as const;
export type LogicJoin = (typeof LOGIC_JOINS)[number];
/** Groups inside groups, counting the top one. */
export const MAX_LOGIC_DEPTH = 3;
export const MAX_LOGIC_ITEMS = 20;
/** Segments one computed question may hold (SOTG's have up to a few hundred). */
export const MAX_SEGMENTS = 1000;

/** Answers picked on a choice question, a grid row, or a grid (its named rows, or all). */
export const AnswerConditionSchema = z
    .object({
        kind: z.literal('answer'),
        question_id: z.string().min(1),
        option_codes: z.array(z.number().int()),
        match: z.enum(LOGIC_MATCHES),
        /** On a grid: 1-based row numbers to read; absent = every row. */
        row_codes: z.array(z.number().int().min(1)).optional(),
        /** On a grid: rows named by their text (from an imported questionnaire). */
        row_text: z.array(z.string()).optional(),
    })
    .strict();

/**
 * A number answer (slider, number pad) compared with a value. On a grid row,
 * the code of the answer picked on that row (SOTG's `ROWCOMP`).
 */
export const NumberConditionSchema = z
    .object({
        kind: z.literal('number'),
        question_id: z.string().min(1),
        op: z.enum(LOGIC_COMPARE_OPS),
        value: z.number(),
    })
    .strict();

/** An open-text answer that contains the words, ignoring case. */
export const TextConditionSchema = z
    .object({
        kind: z.literal('text'),
        question_id: z.string().min(1),
        contains: z.string().min(1),
    })
    .strict();

/**
 * How many answers were picked on a multiple-choice question; on a grid with
 * `option_code`, how many rows were answered with that answer (never true
 * before the grid is answered, as in SOTG).
 */
export const CountConditionSchema = z
    .object({
        kind: z.literal('count'),
        question_id: z.string().min(1),
        op: z.enum(LOGIC_COMPARE_OPS),
        value: z.number().int().min(0),
        option_code: z.number().int().optional(),
    })
    .strict();

export const RANK_MATCHES = ['top', 'exactly', 'bottom'] as const;

/**
 * Where an answer was ranked on a ranking question: in the top `position`,
 * exactly at `position`, or in the bottom `position` of those ranked. False
 * when it was not ranked. SOTG's `Q-k%n%m`.
 */
export const RankConditionSchema = z
    .object({
        kind: z.literal('rank'),
        question_id: z.string().min(1),
        option_code: z.number().int(),
        position: z.number().int().min(1).max(100),
        match: z.enum(RANK_MATCHES),
    })
    .strict();

/** The answer, as typed or known (a zip code), is exactly one of these. SOTG's `Q|LIST|…`. */
export const ListConditionSchema = z
    .object({
        kind: z.literal('list'),
        question_id: z.string().min(1),
        values: z
            .array(
                z
                    .string()
                    .min(1)
                    .max(40)
                    .regex(/^[^,|]+$/),
            )
            .min(1)
            .max(5000),
    })
    .strict();

/**
 * The store the respondent was at (a location survey): its chain is one of
 * these. SOTG's `LOCATIONGROUP-<group id>`; publish maps each chain to its
 * location group. Only on a survey with `location`.
 *
 * A survey read from SOTG names its location groups directly (`group_ids`,
 * with no chains): kept as SOTG has them, so they write back the same.
 */
export const LocationConditionSchema = z
    .object({
        kind: z.literal('location'),
        chain_ids: z.array(z.string().min(1)).max(20),
        group_ids: z.array(z.number().int().min(1)).min(1).max(50).optional(),
    })
    .strict()
    .refine((c) => c.chain_ids.length > 0 || (c.group_ids?.length ?? 0) > 0, {
        message: 'A store condition names a chain or a location group.',
    });

export type AnswerCondition = z.infer<typeof AnswerConditionSchema>;
export type LocationCondition = z.infer<typeof LocationConditionSchema>;
export type NumberCondition = z.infer<typeof NumberConditionSchema>;
export type TextCondition = z.infer<typeof TextConditionSchema>;
export type CountCondition = z.infer<typeof CountConditionSchema>;
export type RankCondition = z.infer<typeof RankConditionSchema>;
export type ListCondition = z.infer<typeof ListConditionSchema>;
/** A condition on a question's answer: everything but `location`. */
export type QuestionCondition =
    | AnswerCondition
    | NumberCondition
    | TextCondition
    | CountCondition
    | RankCondition
    | ListCondition;
export type LogicCondition = QuestionCondition | LocationCondition;

export interface LogicGroup {
    kind: 'group';
    join: LogicJoin;
    items: LogicNode[];
}
export type LogicNode = LogicCondition | LogicGroup;

export const LogicGroupSchema: z.ZodType<LogicGroup> = z.lazy(() =>
    z
        .object({
            kind: z.literal('group'),
            join: z.enum(LOGIC_JOINS),
            items: z
                .array(
                    z.union([
                        AnswerConditionSchema,
                        NumberConditionSchema,
                        TextConditionSchema,
                        CountConditionSchema,
                        RankConditionSchema,
                        ListConditionSchema,
                        LocationConditionSchema,
                        LogicGroupSchema,
                    ]),
                )
                .max(MAX_LOGIC_ITEMS),
        })
        .strict(),
);

/** After this question, go to a later one: always, or when the condition holds. */
export const SkipToSchema = z
    .object({
        to_question_id: z.string().min(1),
        when: LogicGroupSchema.optional(),
    })
    .strict();
export type SkipTo = z.infer<typeof SkipToSchema>;

export const OPTION_ROTATIONS = ['rotate', 'flip'] as const;
export type OptionRotation = (typeof OPTION_ROTATIONS)[number];
export const ANSWER_FORMATS = ['email', 'number', 'zip', 'phone'] as const;
export type AnswerFormat = (typeof ANSWER_FORMATS)[number];
export const DATE_MODES = ['date', 'time', 'datetime', 'duration', 'future_date'] as const;
export type DateMode = (typeof DATE_MODES)[number];

export const BuilderQuestionSchema = z
    .object({
        /** Stable from creation; logic references it, so it never changes on reorder. */
        question_id: z.string().min(1).max(40),
        question_text: z.string(),
        question_type: z.enum(QUESTION_TYPES),
        options: z.array(QuestionOptionSchema),
        options_randomize: z.boolean(),
        is_demographic_reference: z.boolean().optional(),
        slider_min: z.number().int().optional(),
        slider_max: z.number().int().optional(),
        slider_min_label: z.string().optional(),
        slider_max_label: z.string().optional(),
        /** Where the slider starts (SOTG `startval`). */
        slider_start: z.number().int().optional(),
        /** Extra HTML shown with the question (SOTG `questionText`). */
        body_html: z.string().max(65_000).optional(),
        /** SOTG `keyQuestion`. */
        key_question: z.literal(true).optional(),
        /** The SOTG question id of the demographic question this copies (SOTG `demographic`). */
        demographic_ref: z.number().int().min(1).optional(),
        select_min: z.number().int().optional(),
        select_max: z.number().int().optional(),
        display_logic: DisplayLogicSchema.optional(),
        /** The condition tree, when display_logic cannot say it. Never both. */
        show_if: LogicGroupSchema.optional(),
        /** Checked once the question is answered: true ends the survey (screened out). */
        end_survey_if: LogicGroupSchema.optional(),
        skip_to: SkipToSchema.optional(),
        /** An end point that screens out as a quality failure (SOTG `qualityscreen`). */
        quality_screen: z.literal(true).optional(),
        /** A point SOTG keeps with no question id (as its staff make them): published with none. */
        no_question_id: z.literal(true).optional(),
        /**
         * A grid row's key in SOTG when it is not its place in the grid (SOTG's
         * staff number rows as they please). Rules in Builder still name rows by
         * their place; publish and the read translate.
         */
        row_code: z.number().int().min(1).optional(),
        /** A row of a grid whose rows are carried forward, shown whatever was picked (SOTG type 2). */
        row_always_shown: z.literal(true).optional(),
        /**
         * A phased study (study.ts): the phases this question asks in, ascending.
         * Absent asks in every phase.
         */
        phases: z.array(z.number().int().min(1).max(12)).min(1).max(12).optional(),
        // --- Question controls (gap map S1, SOTG editor parity) ---
        /** Answering is optional; absent, an answer is required (SOTG `optional`). */
        optional: z.literal(true).optional(),
        /** Answer order beyond `options_randomize`: rotate the start, or flip for half. */
        options_rotation: z.enum(OPTION_ROTATIONS).optional(),
        /** Consecutive questions with the same group are asked in random order (SOTG `qrotation`). */
        rotate_group: z.string().min(1).max(40).optional(),
        /** Open text: fewest and most characters, paste allowed, a format, and asked twice to confirm. */
        min_chars: z.number().int().min(1).max(10_000).optional(),
        max_chars: z.number().int().min(1).max(10_000).optional(),
        no_paste: z.literal(true).optional(),
        answer_format: z.enum(ANSWER_FORMATS).optional(),
        verify_answer: z.literal(true).optional(),
        /** Number pad: the allowed range and a unit shown beside it. */
        number_min: z.number().optional(),
        number_max: z.number().optional(),
        unit_text: z.string().max(40).optional(),
        /** Date question: what is asked, and its bounds (YYYY-MM-DD). */
        date_mode: z.enum(DATE_MODES).optional(),
        date_min: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
        date_max: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .optional(),
        /** Grid: each column can be used by one row only (ranking; SOTG `uniqueSelections`). */
        unique_per_column: z.literal(true).optional(),
        /** An attention check: any answer outside these codes screens the respondent out as quality. */
        quality_check: z
            .object({ pass_codes: z.array(z.number().int()).min(1) })
            .strict()
            .optional(),
        /** A profile question asked even when the panel already knows the answer. */
        always_ask: z.literal(true).optional(),
        /** Text shown above the question. */
        pre_text: z.string().max(2000).optional(),
        /** Video: the shortest watch before Next, and whether it starts by itself. */
        min_view_seconds: z.number().int().min(1).max(3600).optional(),
        autoplay: z.literal(true).optional(),
        // --- The SOTG editor's other types (gap map S7) ---
        /** Point allocation: the points to split across the answers (SOTG apportionment total). */
        allocation_total: z.number().int().min(1).max(100_000).optional(),
        /** Ranking: how many answers to rank; absent ranks them all. */
        rank_count: z.number().int().min(1).max(100).optional(),
        /** Barcode scan: a product barcode or a QR code. */
        scan_kind: z.enum(['barcode', 'qr']).optional(),
        /** External survey: where respondents go, and what their return counts as. */
        external_url: z.string().url().max(2000).optional(),
        external_return: z.enum(['complete', 'continue']).optional(),
        /**
         * How the app opens it (SOTG externalSurveyType): natively as true mobile web
         * (absent, 0), natively as mobile friendly (1, SOTG's editor's default) or in
         * the phone's browser (2).
         */
        external_opens: z.enum(['mobile_friendly', 'browser']).optional(),
        /** Audio or video (dial): the media to play, once uploaded. */
        media_url: z.string().url().max(2000).optional(),
        /**
         * Segmentation (SOTG's computed question): each segment's rule, in the
         * order of the question's `options`, which are the segments' answers (label
         * and code), so rules, pipes and quotas read a segment like any answer. A
         * rule with no conditions is a segment set by hand, never by a rule.
         */
        segments: z
            .array(
                z
                    .object({
                        label: z.string().min(1).max(200),
                        when: z.lazy(() => LogicGroupSchema),
                        /** A state segment's two-letter key (SOTG keys states by it, not a number). */
                        key: z
                            .string()
                            .regex(/^[A-Z]{2}$/)
                            .optional(),
                    })
                    .strict(),
            )
            .max(MAX_SEGMENTS)
            .optional(),
        /**
         * Segments filled from the panel, not by rules (SOTG's computed questions that
         * read the birthday or zip): age bands named "18 - 24" or "55+", states by
         * their key, or markets by their code.
         */
        segments_from: z.enum(['age', 'state', 'dma']).optional(),
        /** Which segments a respondent gets: the first whose rule holds (absent), or every one. */
        segments_pick: z.enum(['first', 'every']).optional(),
        /**
         * A one-answer question the survey answers for the respondent, who never sees it:
         * the answer with the fewest respondents so far, among those still open
         * (`least_filled`, SOTG's display limit of one), or one at random (`random`, SOTG's
         * randomized single selection). Rules, pipes and quotas read it like any answer.
         */
        assigned: z.enum(['least_filled', 'random']).optional(),
        /** Shown on the same page as the question before it (SOTG multi-item pages). */
        page_with_previous: z.literal(true).optional(),
        /**
         * The standard question this asks (the construct taxonomy: topic.measure.scale),
         * so calibration answers reach the population model already mapped.
         */
        construct_id: z
            .string()
            .regex(/^[a-z0-9_]+(\.[a-z0-9_]+){1,3}$/, 'Use topic.measure.scale, lowercase.')
            .optional(),
        /**
         * A split ballot: respondents are assigned at random to one of `of` arms of
         * the named split, and see only the questions of their arm.
         */
        split: z
            .object({
                name: z.string().min(1).max(40),
                arm: z.number().int().min(1).max(4),
                of: z.number().int().min(2).max(4),
            })
            .strict()
            .optional(),
        /** A profile question from the library: the panel field it asks (question-library.ts). */
        profile_key: z.string().min(1).max(60).optional(),
        /** Free text read from an imported questionnaire; kept and published, not run. */
        terminate_logic: z.string().optional(),
        shared_list_source_qid: z.string().optional(),
        shared_list_mode: z.enum(SHARED_LIST_MODES).optional(),
        shared_list_order_match: z.boolean().optional(),
        // Loops are carried verbatim for publish; their shape is mfour-agents'.
        per_respondent_slot_iteration: z.record(z.unknown()).optional(),
        per_block_iteration: z.record(z.unknown()).optional(),
        per_question_iteration: z.record(z.unknown()).optional(),
    })
    .strict();
export type BuilderQuestion = z.infer<typeof BuilderQuestionSchema>;

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

/**
 * Audience quotas: the feasibility engine's own quota cells (Amendment 4), so
 * the estimate and the fielded survey read one definition. Held in the
 * engine's shape but loosely, so a cell half typed (no name yet, an age being
 * entered) still saves; validateDraft checks it against the engine's own
 * QuotasSchema, and only a draft that passes is sent to feasibility.
 */
const LooseQuotaCellSchema = z.object({
    label: z.string().max(80),
    values: z.array(z.string()).max(60).optional(),
    age: z.object({ min: z.number().int(), max: z.number().int() }).optional(),
    target: z.number().int().min(0).max(1_000_000).optional(),
    share: z.number().min(0).max(1).optional(),
});
export const BuilderQuotasSchema = z.object({
    mode: z.enum(['independent', 'interlocked']),
    dimensions: z
        .array(
            z.object({
                kind: z.enum(QUOTA_DIMENSIONS),
                cells: z.array(LooseQuotaCellSchema).max(12),
            }),
        )
        .max(3),
});
export type BuilderQuotas = z.infer<typeof BuilderQuotasSchema>;

export const BuilderAudienceSchema = z.object({
    /** The engine's own criteria (demographic, app usage, web visit). Never screener answers. */
    criteria: z.array(CriterionSchema).max(12),
    /** Who to cap by profile: gender, age, region, state, DMA. */
    quotas: BuilderQuotasSchema.optional(),
});
export type BuilderAudience = z.infer<typeof BuilderAudienceSchema>;

/**
 * What a release group can hold: SOTG makes none smaller than 100 and splits
 * one bigger than 50,000 (FeasibilityElasticController::createReleaseGroups);
 * the Juicer clamps to the same ceiling.
 */
export const RELEASE_GROUP_BOUNDS = { min: 100, max: 50_000 } as const;

/**
 * Fielding inputs for feasibility and price. Nullable while a draft is being
 * written; validateDraft says what is still missing.
 */
export const BuilderFieldingSchema = z.object({
    /** Completes wanted. Null = ceiling mode. */
    target: z.number().int().min(1).max(1_000_000).nullable(),
    fieldDays: z.number().int().min(1).max(365).nullable(),
    /** Stated by the person, 0–1. Screener questions lower it; the engine never computes it. */
    incidenceRate: z.number().gt(0).max(1).nullable(),
    /** Forced true at feasibility time when a capture question is present. */
    mediaCapture: z.boolean(),
    /**
     * The most people one Juicer release group for this survey may hold
     * (SOTG surveys.maxReleaseGroupSize). Absent = the Juicer's deployment
     * default. Staff only: launch sends it only for staff.
     */
    maxReleaseGroupSize: z
        .number()
        .int()
        .min(RELEASE_GROUP_BOUNDS.min)
        .max(RELEASE_GROUP_BOUNDS.max)
        .optional(),
    /**
     * Who is left out for recent surveys: the feasibility engine's own lockout,
     * so the count and the fielded survey agree (`client` over the MFour account,
     * 30 days, is "one calibration study per panelist a month").
     */
    lockout: LockoutSchema.optional(),
    /** Fielded behind client surveys when low. Staff only: launch sends it only for staff. */
    priority: z.enum(['normal', 'low']).optional(),
});
export type BuilderFielding = z.infer<typeof BuilderFieldingSchema>;

export const MAX_QUESTIONS = 300;

// ---------------------------------------------------------------------------
// Store locations (gap map D, package L2)
// ---------------------------------------------------------------------------

/** The feasibility study types that are store studies (engine storeTargeting). */
export const LOCATION_STUDY_TYPES = [
    'in_store_intercept',
    'exit_interview',
    'exit_interview_24h',
    'directed_shop',
] as const;
export type LocationStudyType = (typeof LOCATION_STUDY_TYPES)[number];

/**
 * When SOTG invites: while the respondent is at the store (after they have
 * stayed the minimum visit), once they leave, or never (invited another way,
 * as a directed shop is).
 */
export const LOCATION_PUSH_MODES = ['at_store', 'after_visit', 'no_push'] as const;
export type LocationPushMode = (typeof LOCATION_PUSH_MODES)[number];

/**
 * What a location survey needs to field. The chains and the minimum visit are
 * the audience's `venue` criterion, not repeated here; validateDraft holds the
 * two together. Minutes throughout, as SOTG stores them.
 */
export const BuilderLocationSchema = z
    .object({
        study_type: z.enum(LOCATION_STUDY_TYPES),
        push: z.enum(LOCATION_PUSH_MODES),
        /** How long the invite stays open: after arriving plus the visit, or after leaving. */
        invite_minutes: z.number().int().min(5).max(10_080),
        /** Reminder pushes, minutes after the first. */
        second_push_minutes: z.number().int().min(1).max(10_080).optional(),
        third_push_minutes: z.number().int().min(1).max(10_080).optional(),
        /** After the visit: visits longer than this do not count. */
        max_visit_minutes: z.number().int().min(1).max(1_440).optional(),
        /** Most completes from any one store. */
        per_store_limit: z.number().int().min(1).max(100_000).optional(),
    })
    .strict();
export type BuilderLocation = z.infer<typeof BuilderLocationSchema>;

/**
 * A cap on completes from respondents who answer a certain way (a quota on
 * survey answers, P4 answer-quotas). Once `cap` completes match `when`, the
 * next respondent who matches is screened out as over quota. A cell that is
 * one answer on one question publishes as that answer's SOTG `filter`; any
 * other cell publishes as a capped terminate question (builder.md, "Quotas").
 */
export const AnswerQuotaSchema = z
    .object({
        id: z.string().regex(/^AQ\d+$/),
        label: z.string().max(80),
        cap: z.number().int().min(1).max(1_000_000),
        when: LogicGroupSchema,
    })
    .strict();
export type AnswerQuota = z.infer<typeof AnswerQuotaSchema>;
export const MAX_ANSWER_QUOTAS = 100;

export const BuilderDraftContentSchema = z.object({
    schemaVersion: z.literal(DRAFT_SCHEMA_VERSION),
    /** May be empty while being written; validateDraft requires a name to publish. */
    title: z.string().max(160),
    /** "Create under". Validated against the caller's scope on every write. */
    targetOrgId: z.number().int().positive(),
    methodology: z.string().min(1).nullable(),
    audience: BuilderAudienceSchema,
    fielding: BuilderFieldingSchema,
    questions: z.array(BuilderQuestionSchema).max(MAX_QUESTIONS),
    /** A store-location survey (SOTG isLocation); absent for every other survey. */
    location: BuilderLocationSchema.optional(),
    /** A tracker, phased or out-of-home study (study.ts); absent for a one-time survey. */
    study: BuilderStudySchema.optional(),
    /** Screens and behaviour (settings.ts, gap map S2). */
    settings: BuilderSettingsSchema.optional(),
    /**
     * The Salesforce project the survey is created under (SOTG sfProjectId), picked
     * from the projects the person can see. Publish needs it. Drafts saved before it
     * existed may hold it in `admin.salesforce_project` instead (`projectIdOf`).
     */
    project: z
        .object({
            id: z.string().regex(/^\d{1,12}$/, 'A Salesforce project id is a number.'),
            name: z.string().max(200),
        })
        .strict()
        .optional(),
    /** Staff bookkeeping; hidden from clients and kept on their saves (settings.ts). */
    admin: BuilderAdminSchema.optional(),
    /** A calibration study for the population model: its job and holdout (staff). */
    calibration: CalibrationSchema.optional(),
    /** Respondents removed after fielding as too fast or straight-lining grids. */
    quality_rules: QualityRulesSchema.optional(),
    /** What panelists earn; staff only. */
    incentive: BuilderIncentiveSchema.optional(),
    /** Translated text per language, by translationKeys key. */
    translations: TranslationsSchema.optional(),
    /** After a complete: another survey, a release group, a reminder (chains.ts). Staff only. */
    chains: z
        .array(z.lazy(() => SurveyChainSchema))
        .max(20)
        .optional(),
    /** Caps on survey answers; audience quotas are in `audience.quotas`. */
    answer_quotas: z.array(AnswerQuotaSchema).max(MAX_ANSWER_QUOTAS).optional(),
    /** Highest `Q<n>` ever allocated in this draft (allocateQuestionId). Never decreases. */
    questionSeq: z.number().int().min(0),
});
export type BuilderDraftContent = z.infer<typeof BuilderDraftContentSchema>;

/** The Salesforce project id publish sends: the picked project, else the older staff field. */
/**
 * How many questions the survey has, as every screen says it: a grid is one
 * question with its statements, as SOTG holds it, never one per statement row.
 */
export function questionCount(content: Pick<BuilderDraftContent, 'questions'>): number {
    return content.questions.filter((q) => q.question_type !== 'matrix_child').length;
}

export function projectIdOf(
    content: Pick<BuilderDraftContent, 'project' | 'admin'>,
): string | null {
    return content.project?.id ?? content.admin?.salesforce_project?.trim() ?? null;
}

export function emptyDraft(targetOrgId: number, title = 'Untitled survey'): BuilderDraftContent {
    return {
        schemaVersion: DRAFT_SCHEMA_VERSION,
        title,
        targetOrgId,
        methodology: null,
        audience: { criteria: [] },
        fielding: { target: null, fieldDays: null, incidenceRate: null, mediaCapture: false },
        questions: [],
        questionSeq: 0,
    };
}

/**
 * Allocates the next `Q<n>` id and returns the draft with its counter advanced.
 * A number is never handed out twice, including one whose question was deleted,
 * so an id that an old logic reference still names can never come back meaning
 * a different question. Imported ids (`S4b`, `AAWARE`) are kept as they are; n
 * also clears any imported `Q<n>`.
 */
export function allocateQuestionId(content: BuilderDraftContent): {
    id: string;
    content: BuilderDraftContent;
} {
    let max = content.questionSeq;
    for (const q of content.questions) {
        const m = /^Q(\d+)$/.exec(q.question_id);
        if (m) max = Math.max(max, Number(m[1]));
    }
    const n = max + 1;
    return { id: `Q${n}`, content: { ...content, questionSeq: n } };
}

/**
 * One past the highest option code present. Deleting the highest option frees
 * its code for reuse, so deleting an option must also remove every
 * `parent_option_codes` reference to it (the editor's delete does both, in one
 * version), or a stale reference would silently point at the new option.
 */
export function nextOptionCode(options: readonly Pick<QuestionOption, 'option_code'>[]): number {
    return options.reduce((max, o) => Math.max(max, o.option_code), 0) + 1;
}
